/**
 * Helpers for the one-time "Use Gemini notes" card shown to Google Meet users.
 * Gemini notes access is optional, so the card is offered once per user per
 * browser and never nags.
 */

const SEEN_KEY_PREFIX = "starmesh.meetNotesTip.seen.v2:";

/**
 * localStorage key remembering that this user already saw the card.
 *
 * @example
 * meetNotesTipSeenKey("u-1"); // "starmesh.meetNotesTip.seen.v2:u-1"
 */
export const meetNotesTipSeenKey = (userId) =>
  `${SEEN_KEY_PREFIX}${String(userId || "anon").trim() || "anon"}`;

/**
 * @example
 * hasSeenMeetNotesTip("u-1"); // false until markMeetNotesTipSeen("u-1")
 */
export const hasSeenMeetNotesTip = (userId) => {
  try {
    return window.localStorage.getItem(meetNotesTipSeenKey(userId)) === "1";
  } catch (_) {
    return false;
  }
};

/**
 * @example
 * markMeetNotesTipSeen("u-1");
 */
export const markMeetNotesTipSeen = (userId) => {
  try {
    window.localStorage.setItem(meetNotesTipSeenKey(userId), "1");
  } catch (_) {
    // Storage blocked: the card may show again next visit, which is harmless.
  }
};

/**
 * The Google Meet workflow the server says could be offered Gemini notes, if any.
 * Only Meet connectors ever have `can_offer_notes_access` set.
 *
 * @example
 * findMeetNotesOffer([{ source: "google_meet", can_offer_notes_access: true, workflow_id: "w1" }]);
 * // the first workflow
 * findMeetNotesOffer([{ source: "zoom" }]); // null
 */
export const findMeetNotesOffer = (workflows) => {
  if (!Array.isArray(workflows)) return null;
  return (
    workflows.find(
      (workflow) =>
        workflow &&
        String(workflow.source || "").toLowerCase() === "google_meet" &&
        workflow.can_offer_notes_access === true &&
        workflow.active !== false
    ) || null
  );
};
